// InvitePage — /invite/:token public landing.
//
// Lands the recipient of a Chairman's Bazooka with a personalised
// welcome message + a streamlined signup form pre-filled with their
// first name (and email if the invite came via email). If the
// Chairman pre-picked a username for this recipient, the username
// field arrives baked-in and locked, so onboarding skips that step.
// On submit, the register call carries the invite_token; the backend
// redeems it and grants Producer comped for 1 quarter (90 days) +
// 1,600 credits in one atomic transaction.
//
// Error states (404/410) collapse to a clean "this invite is no longer
// valid" message with a fallback CTA to sign up normally.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, PartyPopper, Sparkles, ArrowRight, AlertTriangle, Globe } from "lucide-react";
import api from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";

// Suggest a sensible default handle from the recipient's first name.
const suggestUsername = (firstName) => {
  if (!firstName) return "";
  const base = firstName.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!base) return "";
  // 4-digit suffix so the suggestion rarely collides on first try.
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${base}${suffix}`;
};

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Signup form state
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/invite/${token}`)
      .then((r) => {
        if (!alive) return;
        const data = r.data || {};
        setInvite(data);
        // If the Chairman pre-picked a username for this invite, use it
        // exactly and skip the suggestion path. Otherwise seed the field
        // with a sensible default the recipient can edit.
        if (data.username) {
          setUsername(data.username);
        } else {
          setUsername(suggestUsername(data.first_name));
        }
        if (data.email) setEmail(data.email);
      })
      .catch((err) => {
        if (!alive) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail || "This invitation isn't valid.";
        setLoadError({ status: status || 0, detail });
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!username.trim() || !email.trim() || !password) {
      toast.error("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/auth/register", {
        username: username.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        password,
        invite_token: token,
      });
      const data = r.data || {};
      // The AuthContext's `login(email, password)` doesn't fit a
      // post-register sign-in, and there's no `setSession()` helper.
      // Cleanest path: drop the access token + user into the same
      // localStorage keys AuthContext reads from, then hard-redirect
      // to /dashboard so the provider re-hydrates on next mount.
      try {
        if (data.access_token) {
          localStorage.setItem("globaleyes_token", data.access_token);
        }
      } catch { /* private-mode storage block — toast still fires below */ }

      const granted = data.chairman_invite_redemption?.total_credits_granted;
      toast.success(
        granted
          ? `🎉 Welcome, ${invite.first_name}! +${granted} credits landed and you're a comped Producer for the next quarter.`
          : "Welcome! Your account is ready.",
        { duration: 6000 },
      );
      // Hard reload into the dashboard so AuthContext picks up the new
      // token. router.navigate alone wouldn't trigger that rehydration.
      window.location.replace("/dashboard?welcome=chairman");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Signup failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
        <Card className="bg-gray-900 border-amber-500/40 max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
            <h1 className="text-white text-xl font-semibold mb-2">Invitation unavailable</h1>
            <p className="text-gray-400 text-sm mb-4">{loadError.detail}</p>
            <Button onClick={() => navigate("/")} className="bg-emerald-600 hover:bg-emerald-500">
              <Globe className="h-4 w-4 mr-1.5" /> Visit GlobalEyes.TV
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-950/50 via-gray-950 to-emerald-950/50 px-4 py-8 flex items-start sm:items-center justify-center">
      <div className="max-w-2xl w-full">
        <Card
          data-testid="chairman-invite-card"
          className="bg-gray-950/70 backdrop-blur border-amber-500/40 shadow-2xl"
        >
          <CardContent className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <PartyPopper className="h-10 w-10 text-amber-300 mx-auto mb-2" />
              <p className="text-amber-300 text-xs uppercase tracking-widest font-semibold mb-1">
                Personal Invitation
              </p>
              <h1 className="text-white text-2xl sm:text-3xl font-bold leading-tight">
                Hi {invite.first_name}, the Founder & Chairman of GlobalEyes.TV
                <span className="text-amber-300"> personally invites you</span> to join.
              </h1>
            </div>

            <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 sm:p-5 mb-6 space-y-2">
              <p className="text-gray-200 text-sm sm:text-base leading-relaxed">
                You've been granted:
              </p>
              <ul className="text-gray-200 text-sm sm:text-base space-y-1.5">
                <li className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span><strong className="text-white">Producer subscription — 1 quarter (90 days) comped</strong> on the Chairman.
                  Operates exactly like a paid Producer sub, you just skip the quarterly fee this time around.</span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span><strong className="text-white">1,000-credit signing bonus</strong> dropped into your wallet
                  the moment you sign up — on top of the quarter's normal Producer credit allocation.</span>
                </li>
                <li className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span>A producer dashboard with map distribution, X loyalty bonuses, and a marketplace for licensable broadcasts.</span>
                </li>
              </ul>
              <p className="text-gray-400 text-xs mt-3">
                After 90 days, the comped quarter ends — to keep going as a Producer you'll renew with a real card,
                same as any paid Producer. Any future top-ups also require a card; the founder gift is one quarter, not a permanent free tier.
              </p>
              <p className="text-gray-300 text-sm sm:text-base italic mt-3 border-l-2 border-amber-500/60 pl-3">
                The world is waiting for you. Go get 'em.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="chairman-invite-form">
              <div>
                <label className="text-gray-300 text-xs block mb-1">
                  {invite.username ? "Your username (pre-picked by the Chairman)" : "Pick a public username"}
                </label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="yourhandle"
                  data-testid="chairman-invite-username"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="username"
                  readOnly={!!invite.username}
                />
                {invite.username && (
                  <p className="text-gray-500 text-[11px] mt-1">
                    Locked to the handle the Chairman set for you — one less thing to choose.
                  </p>
                )}
              </div>
              <div>
                <label className="text-gray-300 text-xs block mb-1">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="chairman-invite-email"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="email"
                  readOnly={!!invite.email}
                />
                {invite.email && (
                  <p className="text-gray-500 text-[11px] mt-1">
                    Locked to the email this invite was sent to.
                  </p>
                )}
              </div>
              <div>
                <label className="text-gray-300 text-xs block mb-1">Choose a strong password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  data-testid="chairman-invite-password"
                  className="bg-gray-900/60 border-gray-700 text-white"
                  autoComplete="new-password"
                />
                <p className="text-gray-500 text-[11px] mt-1">
                  At least 12 characters, mix of letters/numbers/symbols.
                </p>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="chairman-invite-submit"
                className="w-full bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-400 hover:to-emerald-400 text-gray-900 font-bold h-12 text-base"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <PartyPopper className="h-5 w-5 mr-2" />
                )}
                Claim my Producer quarter
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <p className="text-gray-500 text-[11px] text-center">
                Invited by <span className="text-gray-300">@{invite.fired_by_username}</span> · Invitation expires {new Date(invite.expires_at).toLocaleDateString()}.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvitePage;
